import { faKey, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import { Alert, Col, Form, Row } from 'react-bootstrap';
import mslogo from 'assets/img/logos/microsoftlogo.svg';
import { type FormEvent, useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from 'react-oauth2-code-pkce';

import { useAppSelector, useAppDispatch } from 'redux/store';
import { useAppContext } from 'providers/AppProvider';

import { login } from '../../../redux/slices/authThunk';
import { getReturnToFromSearch, rememberPostAuthRedirect } from 'utils/postAuthRedirect';
import { type LoginFailure, loginFailureMessage } from './loginFailure';
import { log } from 'telemetry/logger';

/** A rejection that carried no descriptor at all -- see the slice's note. */
const UNKNOWN_LOGIN_FAILURE: LoginFailure = { status: undefined, code: undefined };

const SignInForm = () => {
  const authContext = useContext(AuthContext);
  const [username, setusername] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { setSSOLogin } = useAppContext();
  // `loginInProgress`, not the shared `loading`: that one is also set by
  // `fetchUserData`, which has no timeout and is dispatched from MainLayout
  // whenever a token is present. A stale token plus a down backend would
  // otherwise leave this form disabled behind a spinner indefinitely, with
  // nothing said -- issue #1196's own symptom, one flag over.
  const { loginInProgress, loginError } = useAppSelector((state) => state.auth);
  // A failure from the OTHER sign-in path. The SSO callback cannot show
  // anything itself -- it redirects here -- so it hands its message over in
  // router state. Dropped as soon as the user tries again, so a stale SSO
  // message never sits above the result of a password attempt.
  const [ssoFailure, setSsoFailure] = useState<string | undefined>();
  const failureMessage = loginError ? loginFailureMessage(loginError) : ssoFailure;
  const returnTo = getReturnToFromSearch(location.search) || '/';

  // Consume the hand-over once, and strip it from the history entry.
  //
  // NOTE: read here rather than in the `useState` initializer above. The
  // browser persists `history.state`, so a reload of /login is a fresh mount
  // carrying the SAME message -- and an initializer would show it again, for
  // an attempt the user has since abandoned. Reacting to it also means a
  // message delivered to an ALREADY-MOUNTED form is not dropped on the floor,
  // which an initializer that has already run would do.
  useEffect(() => {
    const handedOver = (location.state as { authError?: string } | null)?.authError;
    if (!handedOver) {
      return;
    }
    setSsoFailure(handedOver);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.state, location.pathname, location.search, navigate]);

  const loginWithAzureAD = () => {
    rememberPostAuthRedirect(returnTo);
    localStorage.setItem('SSOLogin', 'true');
    setSSOLogin?.(true);
    authContext?.logIn(); // Start the login flow
  };

  // Log in a user using username and password
  const logIn = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSsoFailure(undefined);
    localStorage.setItem('SSOLogin', 'false');
    setSSOLogin?.(false);

    const resultAction = await dispatch(login({ username, password }));
    if (login.fulfilled.match(resultAction)) {
      navigate(returnTo);
      return;
    }
    // Log the outcome only. The rejected action carries `meta.arg` -- the
    // original `{ username, password }` -- so the two known fields are read
    // out by name and the action itself is never passed (issue #1181).
    //
    // `log.error` rather than `console.error`: the console patch JSON-encodes
    // an object argument INTO the message, so `status` and `code` would only
    // be reachable by a regex over the message body. As attributes they are a
    // filter. This is the only record a failed sign-in produces anywhere --
    // `shouldSkipAuthRefresh` matches /users/login, so the response
    // interceptor returns before `logHttpFailure` ever sees it.
    //
    // Both fields say 'unknown' rather than going out as `undefined`: an
    // explicit value is queryable, where the literal string "undefined" an
    // absent one now ships as is noise. `code` is axios's locale-independent
    // signal (ERR_NETWORK vs ERR_BAD_REQUEST), so a statusless failure can
    // still be told apart.
    const failure = resultAction.payload ?? UNKNOWN_LOGIN_FAILURE;
    log.error('sign-in failed', { source: 'auth', status: failure.status ?? 'unknown', code: failure.code ?? 'unknown' });
  };

  return (
    <>
      <div className="text-center mb-7">
        <h3 className="text-1000">Sign In</h3>
        <p className="text-700">Get access to your account</p>
      </div>
      <div>
        <Button
          variant="phoenix-primary"
          className="w-100 mb-3 btn azure-button d-flex align-items-center"
          onClick={loginWithAzureAD}
          disabled={loginInProgress}
        >
          <img src={mslogo} alt="Azure Logo" className="azure-logo" height={20} /> &nbsp;&nbsp;&nbsp;&nbsp; Login with Azure AD
        </Button>
      </div>
      {/* `Alert` carries role="alert" itself, so appearing here announces it. */}
      {failureMessage && <Alert variant="danger">{failureMessage}</Alert>}
      <Form onSubmit={logIn}>
        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="email">Username</Form.Label>
          <div className="form-icon-container">
            <Form.Control
              id="email"
              type="username"
              className="form-icon-input"
              placeholder="johndoe"
              value={username}
              onChange={(ev) => setusername(ev.target.value)}
            />
            <FontAwesomeIcon icon={faUser} className="text-900 fs-9 form-icon" />
          </div>
        </Form.Group>
        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="password">Password</Form.Label>
          <div className="form-icon-container">
            <Form.Control
              id="password"
              type="password"
              className="form-icon-input"
              placeholder="Password"
              value={password}
              onChange={(ev) => setPassword(ev.target.value)}
            />
            <FontAwesomeIcon icon={faKey} className="text-900 fs-9 form-icon" />
          </div>
        </Form.Group>
        <Row className="flex-between-center mb-7">
          <Col xs="auto">
            <Form.Check type="checkbox" className="mb-0">
              <Form.Check.Input type="checkbox" name="remember-me" id="remember-me" defaultChecked />
              <Form.Check.Label htmlFor="remember-me" className="mb-0">
                Remember me
              </Form.Check.Label>
            </Form.Check>
          </Col>
        </Row>
        <Button variant="primary" className="w-100 mb-3" type="submit" loading={loginInProgress} loadingPosition="start">
          Sign In
        </Button>
      </Form>
    </>
  );
};

export default SignInForm;
